import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore, storage } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

/* ---------- UI ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const Label = styled.div`
  font-weight: 900;
  margin: 10px 0 6px;
  color: #111827;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Danger = styled(Btn)`
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  border: 1px solid rgba(233, 78, 27, 0.28);
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const ItemTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Thumb = styled.img`
  width: 84px;
  height: 64px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #000;
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

const AmountPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 950;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
`;

const SmallNote = styled.div`
  margin-top: 10px;
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
`;

/* ---------- Helpers ---------- */

const toNumber = (v) => {
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const safeLink = (v) => {
  const s = (v || "").trim();
  return s.length ? s : null;
};

function makeStoragePath(file) {
  const safeName = (file?.name || "sponsor")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.\-_]/g, "");
  return `sponsors/${Date.now()}-${safeName}`;
}

/* ---------- Component ---------- */

export default function AdminSponsors() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // form
  const [paymentAmount, setPaymentAmount] = useState("10");
  const [sponsorLink, setSponsorLink] = useState("");
  const [file, setFile] = useState(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);

  const load = async () => {
    const snap = await getDocs(collection(firestore, "sponsors"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Sortera: nyast först om createdAt finns, annars fallback på id
    list.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      if (bt !== at) return bt - at;
      return String(b.id).localeCompare(String(a.id));
    });

    setItems(list);
  };

  useEffect(() => {
    load();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setEditingId(null);
    setPaymentAmount("10");
    setSponsorLink("");
    setFile(null);
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setPaymentAmount(String(it.paymentAmount ?? 10));
    setSponsorLink(it.sponsorLink || "");
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadSponsorImageIfNeeded = async () => {
    if (!file) return { imageUrl: null, storagePath: null };

    const path = makeStoragePath(file);
    const r = ref(storage, path);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    return { imageUrl: url, storagePath: path };
  };

  const save = async () => {
    const amount = toNumber(paymentAmount);

    // om man skapar ny: måste ha fil
    if (!editingId && !file) return alert("Välj en bild att ladda upp.");

    setBusy(true);
    try {
      const uploaded = await uploadSponsorImageIfNeeded();

      if (editingId) {
        // om ny bild laddats upp vid redigering: radera gamla filen (om storagePath finns)
        const old = items.find((x) => x.id === editingId);
        if (uploaded.storagePath && old?.storagePath) {
          try {
            await deleteObject(ref(storage, old.storagePath));
          } catch {
            // ignore
          }
        }

        const payload = {
          paymentAmount: amount,
          sponsorLink: safeLink(sponsorLink),
          updatedAt: serverTimestamp(),
        };

        if (uploaded.imageUrl) {
          payload.imageUrl = uploaded.imageUrl;
          payload.storagePath = uploaded.storagePath;
        }

        await updateDoc(doc(firestore, "sponsors", editingId), payload);
      } else {
        // skapa nytt sponsor-doc
        await addDoc(collection(firestore, "sponsors"), {
          imageUrl: uploaded.imageUrl,
          storagePath: uploaded.storagePath, // ✅ så vi kan radera filen vid delete
          paymentAmount: amount,
          sponsorLink: safeLink(sponsorLink),
          createdAt: serverTimestamp(), // ✅ för sortering framåt
          updatedAt: serverTimestamp(),
        });
      }

      reset();
      await load();
      alert("Sparat!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara sponsor.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Ta bort sponsor?")) return;

    setBusy(true);
    try {
      // 1) ta bort doc
      await deleteDoc(doc(firestore, "sponsors", it.id));

      // 2) ta bort fil om storagePath finns
      if (it.storagePath) {
        try {
          await deleteObject(ref(storage, it.storagePath));
        } catch {
          // ignore
        }
      }

      await load();
      if (editingId === it.id) reset();
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort sponsor.");
    } finally {
      setBusy(false);
    }
  };

  const editingItem = editingId ? items.find((x) => x.id === editingId) : null;

  return (
    <AdminShell title="Sponsorer (upload)">
      <Grid>
        <Card>
          <h3 style={{ margin: 0 }}>{editingId ? "Redigera sponsor" : "Lägg till sponsor"}</h3>

          <Label>Bild (ladda upp)</Label>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />

          {(previewUrl || editingItem?.imageUrl) && (
            <div style={{ marginTop: 10 }}>
              <Thumb
                src={previewUrl || editingItem?.imageUrl}
                alt="Preview"
                style={{ width: 220, height: 140 }}
              />
            </div>
          )}

          <Label>paymentAmount</Label>
          <Input
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="10"
          />

          <Label>sponsorLink (valfritt)</Label>
          <Input
            value={sponsorLink}
            onChange={(e) => setSponsorLink(e.target.value)}
            placeholder="https://..."
          />

          <Row>
            <Btn type="button" onClick={save} disabled={busy}>
              {busy ? "Sparar…" : editingId ? "Spara" : "Lägg till"}
            </Btn>

            <Ghost type="button" onClick={reset} disabled={busy}>
              Rensa
            </Ghost>
          </Row>

          <SmallNote>
            ✅ Sparar i <b>collection</b> <code>sponsors</code> med fälten{" "}
            <b>imageUrl</b>, <b>paymentAmount</b>, <b>sponsorLink</b>. <br />
            ✅ Jag lägger även <b>storagePath</b>, <b>createdAt</b>, <b>updatedAt</b>.
          </SmallNote>
        </Card>

        <Card>
          <h3 style={{ margin: 0 }}>Lista</h3>
          <div style={{ height: 10 }} />

          {items.length === 0 ? (
            <Muted>Inga sponsorer ännu.</Muted>
          ) : (
            items.map((it) => (
              <Item key={it.id}>
                <ItemTop>
                  <Thumb src={it.imageUrl} alt="Sponsor" />
                  <div style={{ display: "grid", gap: 6 }}>
                    <AmountPill>paymentAmount: {Number(it.paymentAmount ?? 0)} kr</AmountPill>
                    <Muted>
                      sponsorLink:{" "}
                      {it.sponsorLink ? (
                        <a href={it.sponsorLink} target="_blank" rel="noreferrer">
                          {it.sponsorLink}
                        </a>
                      ) : (
                        "null"
                      )}
                    </Muted>
                  </div>
                </ItemTop>

                <Row>
                  <Ghost type="button" onClick={() => startEdit(it)} disabled={busy}>
                    Redigera
                  </Ghost>
                  <Danger type="button" onClick={() => remove(it)} disabled={busy}>
                    Ta bort
                  </Danger>
                </Row>
              </Item>
            ))
          )}
        </Card>
      </Grid>
    </AdminShell>
  );
}


